import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { graphqlService } from '../services/graphqlService';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);

  // Function to reload notifications from GraphQL
  const reloadNotifications = React.useCallback(async () => {
    if (user && user.email) {
      try {
        const notifications = await graphqlService.getNotifications(user.email);
        setNotifications(notifications || []);
      } catch (error) {
        console.error('Error loading notifications from GraphQL:', error);
        setNotifications([]);
      }
    } else {
      // Clear notifications when user logs out
      setNotifications([]);
    }
  }, [user?.email]);

  // Load notifications from localStorage on mount and when user changes
  useEffect(() => {
    reloadNotifications();
  }, [reloadNotifications]);

  // Listen for custom event to reload notifications (triggered when module assignments change)
  useEffect(() => {
    const handleNotificationUpdate = () => {
      reloadNotifications();
    };
    
    window.addEventListener('notificationsUpdated', handleNotificationUpdate);
    
    return () => {
      window.removeEventListener('notificationsUpdated', handleNotificationUpdate);
    };
  }, [reloadNotifications]);

  // No need to save to localStorage - GraphQL handles persistence

  const addNotification = async (notification) => {
    if (!user || !user.email) return;
    
    try {
      const input = {
        userEmail: user.email,
        title: notification.title,
        message: notification.message,
        type: notification.type || 'info',
        moduleId: notification.moduleId || null
      };
      
      const newNotification = await graphqlService.createNotification(input);
      setNotifications(prev => [newNotification, ...prev]);
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await graphqlService.markNotificationAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notif =>
          notif._id === notificationId ? { ...notif, read: true } : notif
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Update locally even if GraphQL fails
      setNotifications(prev =>
        prev.map(notif =>
          notif._id === notificationId ? { ...notif, read: true } : notif
        )
      );
    }
  };

  const markAllAsRead = async () => {
    if (!user || !user.email) return;
    
    try {
      await graphqlService.markAllNotificationsAsRead(user.email);
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, read: true }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Update locally even if GraphQL fails
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, read: true }))
      );
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await graphqlService.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(notif => notif._id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
      // Remove locally even if GraphQL fails
      setNotifications(prev => prev.filter(notif => notif._id !== notificationId));
    }
  };

  const clearAllNotifications = async () => {
    if (!user || !user.email) return;
    
    try {
      await graphqlService.clearAllNotifications(user.email);
      setNotifications([]);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      setNotifications([]);
    }
  };

  const [unreadCount, setUnreadCount] = useState(0);

  // Update unread count from GraphQL
  useEffect(() => {
    const updateUnreadCount = async () => {
      if (user && user.email) {
        try {
          const count = await graphqlService.getUnreadCount(user.email);
          setUnreadCount(count || 0);
        } catch (error) {
          console.error('Error fetching unread count:', error);
          // Fallback to local calculation
          setUnreadCount(Array.isArray(notifications) ? notifications.filter(n => !n.read).length : 0);
        }
      } else {
        setUnreadCount(0);
      }
    };
    
    updateUnreadCount();
    // Also update when notifications change
    if (Array.isArray(notifications)) {
      setUnreadCount(notifications.filter(n => !n.read).length);
    }
  }, [user?.email, notifications]);

  const value = {
    notifications: Array.isArray(notifications) ? notifications : [],
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    reloadNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

